





































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TweeningNumber from '@/components/forms/fields/TweeningNumber';
import smoothReflow from 'vue-smooth-reflow';
import ProductSearch, { VariantClickEvent as SearchVariantClickEvent } from './ProductSearch.vue';
import ProductsTable, { ChangeQuantityEvent as TableChangeQuantityEvent } from './ProductsTable.vue';
import OrderProductVariant from '@/zenky/interfaces/orders/OrderProductVariant';
import ProductVariant from '@/zenky/interfaces/products/ProductVariant';
import Price from '@/zenky/interfaces/common/Price';
import SkeletonText from '@/components/typography/SkeletonText.vue';
import { formatPriceRub } from '@/helpers/utils';

type ChangeQuantityData<T extends 'set' | 'incOrDec' = 'set' | 'incOrDec'> = TableChangeQuantityEvent<T>;
export type ChangeQuantityEvent<T extends 'set' | 'incOrDec' = 'set' | 'incOrDec'> = ChangeQuantityData<T> & {
  resolve: (newQuantity: ProductsTable['orderVariants'][number]['quantity'] | 0) => void,
  reject: (reason?: any) => void,
};

type MyOrderProductVariant = ProductsTable['orderVariants'][number] & OrderProductVariant;

@Component({
  components: { ProductSearch, ProductsTable, SkeletonText, TweeningNumber },
  mixins: [smoothReflow],
})
export default class ProductsSection extends Vue {
  @Prop({ required: true }) readonly orderVariants!: MyOrderProductVariant[] | null;
  @Prop({ required: true }) readonly total!: Price | null;
  @Prop({ type: Number, default: 300 }) readonly debounceTime!: number;
  quantityChangePromises: {
    [variantId: string]: Array<Promise<Parameters<ChangeQuantityEvent['resolve']>[0]>>,
  } = {};

  mounted() {
    this.$smoothReflow!({ el: this.$refs.list as Element, transition: 'height 0.4s' });
  }

  // Modifying setter just to detect incorrect usage. TODO maybe we can somehow do it with just TypeScript?
  private innerIncOrDecDebounceBuffer: { variantId: string, quantityDelta: number } | null = null;
  get incOrDecDebounceBuffer() {
    return this.innerIncOrDecDebounceBuffer;
  }
  set incOrDecDebounceBuffer(newVal) {
    if (this.innerIncOrDecDebounceBuffer !== null && newVal !== null) {
      throw new Error('Attempted to override non-empty buffer. It must be flused first');
    }
    this.innerIncOrDecDebounceBuffer = newVal;
  }

  debounceTimeout: number | undefined = undefined;

  get variantCounts(): ProductSearch['variantCounts'] {
    if (this.orderVariants === null) {
      return null;
    }
    const counts: { [variantId: string]: number } = {};
    this.orderVariants.forEach((variant) => { counts[variant.product_variant_id] = variant.quantity; });
    return counts;
  }

  async emit(data: ChangeQuantityData) {
    if (this.quantityChangePromises[data.variantId] === undefined) {
      this.$set(this.quantityChangePromises, data.variantId, []);
    }
    const promise = new Promise<Parameters<ChangeQuantityEvent['resolve']>[0]>((resolve, reject) => {
      const payload: ChangeQuantityEvent = {
        ...data,
        resolve,
        reject,
      };
      this.$emit('change-variant-quantity', payload);
    });
    this.quantityChangePromises[data.variantId].push(promise);
    await promise;
    if (this.quantityChangePromises[data.variantId].length === 1) {
      this.$delete(this.quantityChangePromises, data.variantId);
    } else {
      this.quantityChangePromises[data.variantId].splice(
        this.quantityChangePromises[data.variantId].indexOf(promise),
        1,
      );
    }
  }
  debounceEmit(data: ChangeQuantityData) {
    if (data.type === 'set') {
      if (this.incOrDecDebounceBuffer !== null && this.incOrDecDebounceBuffer.variantId === data.variantId) {
        this.clearDebounce();
      }
      this.emit(data);
    } else {
      if (this.incOrDecDebounceBuffer !== null) {
        if (this.incOrDecDebounceBuffer.variantId === data.variantId) {
          this.incOrDecDebounceBuffer.quantityDelta += data.quantityDelta;
          if (this.incOrDecDebounceBuffer.quantityDelta === 0) {
            this.clearDebounce();
          }
        } else {
          this.flushDebounce();
          this.incOrDecDebounceBuffer = { variantId: data.variantId, quantityDelta: data.quantityDelta };
        }
      } else {
        this.incOrDecDebounceBuffer = { variantId: data.variantId, quantityDelta: data.quantityDelta };
      }
      // Refresh debounce.
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => this.flushDebounce(), this.debounceTime);
    }
  }
  flushDebounce() {
    if (this.incOrDecDebounceBuffer === null) { throw new Error('Attempted to flush empty buffer'); }
    const data: ChangeQuantityData<'incOrDec'> = {
      type: 'incOrDec',
      variantId: this.incOrDecDebounceBuffer.variantId,
      quantityDelta: this.incOrDecDebounceBuffer.quantityDelta,
    };
    this.emit(data);
    this.clearDebounce();
  }
  clearDebounce() {
    this.incOrDecDebounceBuffer = null;
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = undefined;
  }

  onTableVariantQuantityChange($event: TableChangeQuantityEvent) {
    const data: ChangeQuantityData = $event;
    this.debounceEmit(data);
  }
  onSearchVariantClick($event: SearchVariantClickEvent) {
    const payload: ChangeQuantityData<'incOrDec'> = {
      type: 'incOrDec',
      variantId: $event.id,
      quantityDelta: +1,
    };
    this.debounceEmit(payload);
  }

  get quantityPendingFor(): ProductsTable['quantityPendingFor'] {
    const promisesPendingFor = Object.keys(this.quantityChangePromises);
    if (this.incOrDecDebounceBuffer === null || promisesPendingFor.includes(this.incOrDecDebounceBuffer.variantId)) {
      return promisesPendingFor;
    }
    return [...promisesPendingFor, this.incOrDecDebounceBuffer.variantId];
  }

  private get formatPriceRub() { return formatPriceRub; }
}
